import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Badge, Button, Input, NumberInput, Select, Tab, Table, goldAlert, goldToast } from "gold-ui-kit";
import AppShell from "../components/classic/AppShell";
import ClassicWindow from "../components/classic/ClassicWindow";
import Toolbar, { ToolbarButton, ToolbarSeparator } from "../components/classic/Toolbar";
import {
  addDraftRow,
  updateDraftRow,
  deleteDraftRow,
  clearDrafts,
  setPeriod,
  updateLocalItem,
} from "../store/slices/employees/data-entries";
import {
  fetchDataEntries,
  saveBulkDataEntries,
  updateDataEntry,
  deleteDataEntry,
} from "../store/slices/employees/data-entries/dataEntriesThunks";
import { fetchEmployees } from "../store/slices/employees";
import { useAuth } from "../context/AuthContext";

// unitMode: 'amount' (Tutar ₺), 'hours' (Saat), 'days' (Gün)
const TABS = [
  { key: "avans", dataType: 1, label: "Avans", unitMode: "amount" },
  { key: "mesai", dataType: 2, label: "Mesai (Saat)", unitMode: "hours" },
  { key: "kesinti", dataType: 3, label: "Kesinti Gün", unitMode: "days" },
];

const MONTHS = [
  { value: 1, label: "Ocak" }, { value: 2, label: "Şubat" }, { value: 3, label: "Mart" },
  { value: 4, label: "Nisan" }, { value: 5, label: "Mayıs" }, { value: 6, label: "Haziran" },
  { value: 7, label: "Temmuz" }, { value: 8, label: "Ağustos" }, { value: 9, label: "Eylül" },
  { value: 10, label: "Ekim" }, { value: 11, label: "Kasım" }, { value: 12, label: "Aralık" },
];

const YEARS = (() => {
  const y = new Date().getFullYear();
  const arr = [];
  for (let i = y - 3; i <= y + 1; i++) arr.push({ value: i, label: String(i) });
  return arr;
})();

const STATUS_MAP = {
  1: { label: "Onay Bekliyor", color: "warning" },
  2: { label: "Onaylandı", color: "success" },
  3: { label: "Geri Alındı", color: "default" },
  4: { label: "Reddedildi", color: "danger" },
};

const VeriGirisiPage = () => {
  const dispatch = useDispatch();
  const { user } = useAuth();

  const employees = useSelector((s) => s.employees?.list || []);
  const { items, drafts, periods, loading } = useSelector((s) => s.dataEntry);

  const [activeTab, setActiveTab] = React.useState("avans");
  const tabConfig = TABS.find((t) => t.key === activeTab);
  const period = periods[activeTab];

  useEffect(() => {
    if (user?.branchId) {
      dispatch(fetchEmployees({ branchId: user.branchId, year: period.year, month: period.month }));
      dispatch(fetchDataEntries({ year: period.year, month: period.month, dataType: tabConfig.dataType }));
    }
  }, [dispatch, user?.branchId, period.year, period.month, tabConfig.dataType]);

  const rows = useMemo(() => {
    const apiRows = items.filter((i) => i.dataType === tabConfig.dataType && i.month === period.month && i.year === period.year);
    const draftRows = drafts.filter((d) => d.dataType === tabConfig.dataType && d.month === period.month && d.year === period.year);
    return [...apiRows, ...draftRows];
  }, [items, drafts, tabConfig.dataType, period.month, period.year]);

  const hasChanges = useMemo(() => {
    const hasDrafts = drafts.some((d) => d.dataType === tabConfig.dataType && d.month === period.month && d.year === period.year);
    const hasModified = items.some(
      (i) => i.dataType === tabConfig.dataType && i.month === period.month && i.year === period.year && i._modified
    );
    return hasDrafts || hasModified;
  }, [drafts, items, tabConfig.dataType, period.month, period.year]);

  const activeEmployeeOptions = useMemo(
    () =>
      employees
        .filter((e) => !e.exitDate && !e.ExitDate && !e.resignationDate && !e.ResignationDate)
        .map((e) => ({ value: e.id, label: `${e.name || e.firstName} ${e.surname || e.lastName}` })),
    [employees]
  );

  const upd = (id, field, value) => {
    if (String(id).startsWith("draft-")) {
      dispatch(updateDraftRow({ id, field, value }));
    } else {
      dispatch(updateLocalItem({ id, field, value }));
    }
  };

  const handleSave = async () => {
    const draftsToSave = drafts.filter(
      (d) => d.dataType === tabConfig.dataType && d.month === period.month && d.year === period.year && d.employeeId
    );
    if (draftsToSave.length > 0) {
      const payload = draftsToSave.map((d) => ({
        employeeId: d.employeeId,
        value: Number(d.value) || 0,
        description: d.description,
        dataType: d.dataType,
        month: d.month,
        year: d.year,
      }));
      await dispatch(saveBulkDataEntries(payload));
    }

    const modifiedItems = items.filter(
      (i) => i.dataType === tabConfig.dataType && i.month === period.month && i.year === period.year && i._modified
    );
    for (const item of modifiedItems) {
      const payload = {
        employeeId: item.employeeId,
        value: Number(item.value) || 0,
        description: item.description,
        dataType: item.dataType,
        month: item.month,
        year: item.year,
      };
      await dispatch(updateDataEntry({ id: item.id, data: payload }));
    }

    dispatch(fetchDataEntries({ year: period.year, month: period.month, dataType: tabConfig.dataType }));
    goldToast.success("Kaydetme işlemi tamamlandı.");
  };

  const handleDelete = async (id) => {
    if (String(id).startsWith("draft-")) {
      dispatch(deleteDraftRow({ id }));
      return;
    }
    const result = await goldAlert.fire({
      title: "Kayıt silinsin mi?",
      text: "Bu kaydı silmek istediğinize emin misiniz?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Evet, Sil",
      cancelButtonText: "Vazgeç",
      confirmButtonColor: "danger",
      cancelButtonColor: "default",
    });
    if (result.isConfirmed) await dispatch(deleteDataEntry(id));
  };

  const handleClearDrafts = async () => {
    const result = await goldAlert.fire({
      title: "Taslaklar temizlensin mi?",
      text: "Bu sekmedeki henüz kaydedilmemiş (taslak) satırlar silinecek.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Evet, Temizle",
      cancelButtonText: "Vazgeç",
      confirmButtonColor: "warning",
      cancelButtonColor: "default",
    });
    if (result.isConfirmed) dispatch(clearDrafts({ tab: activeTab }));
  };

  const total = useMemo(() => rows.reduce((acc, r) => acc + Number(r.value || 0), 0), [rows]);

  const empName = (id, row) => {
    if (row && row.employeeName) return `${row.employeeName} ${row.employeeSurname || ""}`.trim();
    const e = employees.find((x) => x.id === id);
    return e ? `${e.name || e.firstName} ${e.surname || e.lastName}` : "";
  };

  const periodLabel = `${MONTHS.find((m) => m.value === period.month)?.label} ${period.year}`;

  const showAmount = tabConfig.unitMode === "amount";
  const showHours = tabConfig.unitMode === "hours";
  const unitColLabel = showAmount ? "Tutar (₺)" : showHours ? "Mesai Saati" : "Gün Sayısı";
  const totalColLabel = showAmount ? "Toplam Tutar" : showHours ? "Toplam Saat" : "Toplam Gün";
  const unitSuffix = showAmount ? "₺" : showHours ? "saat" : "gün";
  const formattedTotal = showAmount
    ? `${total.toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺`
    : `${total} ${unitSuffix}`;

  const tabCount = (t) =>
    items.filter((i) => i.dataType === t.dataType && i.month === period.month && i.year === period.year).length +
    drafts.filter((d) => d.dataType === t.dataType && d.month === period.month && d.year === period.year).length;

  const columns = [
    {
      key: "no",
      dataIndex: "id",
      title: "#",
      width: 48,
      align: "center",
      render: (r) => (String(r.id).startsWith("draft-") ? "*" : rows.indexOf(r) + 1),
    },
    {
      key: "employee",
      dataIndex: "employeeId",
      title: "Personel",
      width: 280,
      render: (r) => (
        <Select
          size="sm"
          searchable
          searchPlaceholder="Personel ara…"
          placeholder="— Personel Seçiniz —"
          emptyMessage="Personel bulunamadı"
          options={activeEmployeeOptions}
          value={r.employeeId || ""}
          onChange={(e) => upd(r.id, "employeeId", e.target.value)}
        />
      ),
    },
    {
      key: "value",
      dataIndex: "value",
      title: unitColLabel,
      width: 170,
      render: (r) => (
        <NumberInput
          size="sm"
          min={0}
          step={showAmount ? 0.01 : showHours ? 0.5 : 1}
          precision={showAmount ? 2 : showHours ? 1 : 0}
          placeholder="0"
          suffix={unitSuffix}
          value={r.value === "" || r.value == null ? undefined : Number(r.value)}
          onChange={(v) => upd(r.id, "value", v === undefined ? "" : v)}
        />
      ),
    },
    {
      key: "description",
      dataIndex: "description",
      title: "Açıklama",
      render: (r) => (
        <Input
          size="sm"
          value={r.description || ""}
          onChange={(e) => upd(r.id, "description", e.target.value)}
          placeholder={r.employeeId ? `${empName(r.employeeId, r)} için açıklama...` : "Açıklama"}
        />
      ),
    },
    {
      key: "status",
      dataIndex: "status",
      title: "Durum",
      width: 130,
      align: "center",
      render: (r) => {
        if (String(r.id).startsWith("draft-")) {
          return (
            <Badge size="sm" radius="full" variant="soft" color="warning">
              Kaydedilmedi
            </Badge>
          );
        }
        const stat = STATUS_MAP[r.status] || STATUS_MAP[1];
        return (
          <Badge size="sm" radius="full" variant="soft" color={r._modified ? "primary" : stat.color}>
            {r._modified ? "Değiştirildi" : stat.label}
          </Badge>
        );
      },
    },
    {
      key: "actions",
      dataIndex: "id",
      title: "İşlem",
      width: 90,
      align: "center",
      render: (r) => (
        <Button size="xs" variant="filled" color="danger" onClick={() => handleDelete(r.id)}>
          Sil
        </Button>
      ),
    },
  ];

  const body = (
    <div className="flex flex-col">
      <div className="flex flex-wrap items-center gap-3 border-y border-slate-100 bg-amber-50/60 px-4 py-3 text-xs">
        <span className="font-bold text-slate-800">📅 Dönem</span>
        <div className="w-32">
          <Select
            size="sm"
            options={MONTHS}
            value={period.month}
            onChange={(e) => dispatch(setPeriod({ tab: activeTab, month: Number(e.target.value), year: period.year }))}
          />
        </div>
        <div className="w-24">
          <Select
            size="sm"
            options={YEARS}
            value={period.year}
            onChange={(e) => dispatch(setPeriod({ tab: activeTab, month: period.month, year: Number(e.target.value) }))}
          />
        </div>
        <span className="text-slate-500">
          Bu sekmedeki tüm kayıtlar <b className="text-slate-800">{periodLabel}</b> dönemine aittir.
        </span>
      </div>

      <Toolbar>
        <ToolbarButton icon="➕" label="Yeni Satır" onClick={() => dispatch(addDraftRow({ tab: activeTab }))} />
        <ToolbarButton
          icon="💾"
          label="Kaydet (Toplu/Güncel)"
          active={hasChanges}
          color="warning"
          className={hasChanges ? "animate-pulse" : ""}
          onClick={handleSave}
        />
        <ToolbarButton icon="🧹" label="Temizle (Yeni Satırlar)" onClick={handleClearDrafts} />
        <ToolbarSeparator />
        <span className="px-1 text-xs text-slate-600">
          <b>{tabConfig.label}</b> — Satır: <b>{rows.length}</b>
        </span>
        <ToolbarSeparator />
        <span className="px-1 text-xs text-slate-600">
          {totalColLabel}: <b className="text-slate-900">{formattedTotal}</b>
        </span>
      </Toolbar>

      <div className="p-3 sm:p-4">
        <Table
          size="sm"
          uniqueKey="id"
          columns={columns}
          dataSource={rows}
          loading={loading ? { variant: "skeleton", skeletonRows: 5 } : false}
          scroll={{ x: 900 }}
          onRow={(r) => ({
            className: String(r.id).startsWith("draft-") || r._modified ? "bg-amber-50/70" : undefined,
          })}
          locale={{ emptyText: "Bu sekmede henüz kayıt yok", emptyHint: '"Yeni Satır" ile veri girişi yapın.' }}
        />

        {rows.length > 0 && (
          <div className="mt-3 flex items-center justify-end gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm">
            <span className="text-slate-500">Toplam ({periodLabel}):</span>
            <span className="font-bold text-slate-900">{formattedTotal}</span>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <AppShell pageId="veri-girisi">
      <ClassicWindow title="Veri Girişi — Avans / Mesai / Kesinti" width="100%">
        <div className="px-4 pt-4">
          <Tab
            variant="filled"
            className="!w-full"
            activeKey={activeTab}
            onChange={(key) => setActiveTab(key)}
            items={TABS.map((t) => ({
              key: t.key,
              title: (
                <span className="flex items-center gap-1.5">
                  {t.label}
                  <Badge size="xs" radius="full" variant="soft" color={t.key === activeTab ? "primary" : "default"}>
                    {tabCount(t)}
                  </Badge>
                </span>
              ),
              content: t.key === activeTab ? <div className="-mx-4">{body}</div> : null,
            }))}
          />
        </div>
      </ClassicWindow>
    </AppShell>
  );
};

export default VeriGirisiPage;
